import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

console.log('=== API CLIENT CONFIGURATION ===');
console.log('apiClient: Environment BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
console.log('apiClient: Fallback BACKEND_URL:', 'http://localhost:8001');
console.log('apiClient: Final BACKEND_URL:', BACKEND_URL);
console.log('apiClient: Full API base URL:', `${BACKEND_URL}/api`);

// Check for mixed content issues
if (typeof window !== 'undefined') {
  const currentProtocol = window.location.protocol;
  const backendProtocol = BACKEND_URL.startsWith('https://') ? 'https:' : 'http:';
  
  console.log('apiClient: Current page protocol:', currentProtocol);
  console.log('apiClient: Backend URL protocol:', backendProtocol);
  
  if (currentProtocol === 'https:' && backendProtocol === 'http:') {
    console.error('=== MIXED CONTENT WARNING ===');
    console.error('apiClient: WARNING - Mixed Content Issue Detected!');
    console.error('apiClient: Frontend is running on HTTPS but backend URL is HTTP');
    console.error('apiClient: This will cause the request to be blocked by the browser');
    console.error('apiClient: Please set REACT_APP_BACKEND_URL to use HTTPS');
    console.error('=== END WARNING ===');
  }
}

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is an MFA verification request - don't redirect for MFA errors
      const isMFAVerification = error.config?.url?.includes('/auth/verify-mfa');
      
      if (isMFAVerification) {
        // For MFA verification errors, let the component handle the error
        console.log('MFA verification failed - letting component handle error');
        return Promise.reject(error);
      }
      
      // Handle unauthorized access for all other 401 errors
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Show error toast for non-401 errors, except 410 for recording completion
    if (error.response?.status !== 401) {
      // Suppress 410 errors for recording completion endpoints (session already completed)
      const isRecordingCompletion = error.config?.url?.includes('/complete-session');
      const is410Error = error.response?.status === 410;
      
      if (isRecordingCompletion && is410Error) {
        // Don't show toast for recording completion 410 errors - this is expected when session is already completed
        console.log('🔇 Suppressed 410 error toast for recording completion - session already completed');
        return Promise.reject(error);
      }
      
      // Extract error message safely
      let errorMessage = 'An error occurred';
      const errorData = error.response?.data;
      
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.detail) {
        errorMessage = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : 'An error occurred';
      } else if (errorData?.message) {
        errorMessage = typeof errorData.message === 'string' 
          ? errorData.message 
          : 'An error occurred';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
    
    return Promise.reject(error);
  }
);

// API service functions
export const authAPI = {
  requestOTP: (email, channel = 'email') => {
    const params = new URLSearchParams();
    params.append('email', email);
    params.append('channel', channel);
    return api.post('/auth/request-otp', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  
  verifyOTP: (email, otpCode) => {
    const params = new URLSearchParams();
    params.append('email', email);
    params.append('otp_code', otpCode);
    return api.post('/auth/verify-otp', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }
};

export const tenantsAPI = {
  getTenants: () => api.get('/tenants'),
  getAllTenants: () => api.get('/admin/tenants'),
  getTenant: (tenantId) => api.get(`/admin/tenants/${tenantId}`),
  createTenant: (tenantData) => {
    const formData = new FormData();
    Object.keys(tenantData).forEach(key => {
      if (tenantData[key] !== null && tenantData[key] !== undefined && tenantData[key] !== '') {
        formData.append(key, tenantData[key]);
      }
    });
    return api.post('/admin/tenants', formData);
  },
  updateTenant: (tenantId, tenantData) => {
    const formData = new FormData();
    Object.keys(tenantData).forEach(key => {
      if (tenantData[key] !== null && tenantData[key] !== undefined && tenantData[key] !== '') {
        formData.append(key, tenantData[key]);
      }
    });
    return api.put(`/admin/tenants/${tenantId}`, formData);
  }
};

export const clinicsAPI = {
  getClinics: () => api.get('/tenants')  // Backward compatibility
};

export const patientsAPI = {
  getPatients: () => api.get('/patients'),
  
  createPatient: (patientData) => {
    const formData = new FormData();
    // Add mandatory fields
    if (!patientData.email || !patientData.phone || !patientData.tenant_id) {
      throw new Error('Email, phone, and tenant are required');
    }
    
    Object.keys(patientData).forEach(key => {
      if (patientData[key] !== null && patientData[key] !== undefined && patientData[key] !== '') {
        formData.append(key, patientData[key]);
      }
    });
    return api.post('/patients', formData);
  },
  
  updatePatient: (patientId, patientData) => {
    const formData = new FormData();
    Object.keys(patientData).forEach(key => {
      if (key !== 'patient_id' && patientData[key] !== null && patientData[key] !== undefined && patientData[key] !== '') {
        formData.append(key, patientData[key]);
      }
    });
    return api.put(`/patients/${patientId}`, patientData);
  },
  
  getPatientVitals: (patientId) => api.get(`/patients/${patientId}/vitals`)
};

export const recordingAPI = {
  getRecordingSession: (token) => api.get(`/recording/${token}`),
  
  getSessionQuestions: (token) => api.get(`/recording/${token}/questions`),
  
  uploadQuestionRecording: (token, responseId, questionId, audioFile, videoFile) => {
    const formData = new FormData();
    formData.append('response_id', responseId);
    formData.append('question_id', questionId);
    if (audioFile) formData.append('audio_file', audioFile);
    if (videoFile) formData.append('video_file', videoFile);
    
    return api.post(`/recording/${token}/upload-question`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  completeSession: (token) => {
    return api.post(`/recording/${token}/complete-session`);
  },
  
  // Legacy endpoint for backward compatibility
  uploadRecording: (token, sessionId, audioFile, videoFile) => {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('audio_file', audioFile);
    formData.append('video_file', videoFile);
    
    return api.post(`/recording/${token}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

export const vitalsAPI = {
  saveSummary: (patientId, summary) => {
    const formData = new FormData();
    formData.append('session_id', summary.session_id);
    if (summary.hr != null) formData.append('hr', String(summary.hr));
    if (summary.hrv != null) formData.append('hrv', String(summary.hrv));
    if (summary.rhythm) formData.append('rhythm', summary.rhythm);
    if (summary.bp_sys != null) formData.append('bp_sys', String(summary.bp_sys));
    if (summary.bp_dia != null) formData.append('bp_dia', String(summary.bp_dia));
    if (summary.quality_flag != null) formData.append('quality_flag', String(summary.quality_flag));
    return api.post(`/patients/${patientId}/vitals`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export default api;